import React from 'react';
import { Clock, LockOpen, AlertCircle, Play } from 'lucide-react';

// Helper component to render the appropriate video player based on URL
const VideoPlayer = ({ url }) => {
  const isYouTube = url.includes('youtube.com') || url.includes('youtu.be');
  const isVimeo = url.includes('vimeo.com');

  if (isYouTube) {
    return (
      <iframe
        className="w-full h-full"
        src={url.replace('watch?v=', 'embed/')}
        title="Video z areálu"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    );
  }

  if (isVimeo) {
    return (
      <iframe
        className="w-full h-full"
        src={url.replace('vimeo.com/', 'player.vimeo.com/video/')}
        title="Video z areálu"
        frameBorder="0"
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
      />
    );
  }

  return (
    <video className="w-full h-full" controls>
      <source src={url} type="video/mp4" />
      Váš prohlížeč nepodporuje video tag.
    </video>
  );
};

const Home = () => {
  // Video URL - Vimeo video z areálu
  const videoUrl = 'https://vimeo.com/1018441132';
  
  return (
    <>
      {/* Hero Section – Vyčištěný skleněný podklad bez vnitřní černé krabice */}
      <section className="relative w-full h-[100vh] h-[100dvh] flex items-center justify-center overflow-hidden bg-white">
        {/* Obrázek jako absolutní pozadí – object-cover zajistí stoprocentní vyplnění displeje */}
        <img 
          src={process.env.PUBLIC_URL + '/uvodni-foto.jpg'} 
          alt="Pískáč Postřelmov" 
          className="absolute inset-0 w-full h-full object-cover object-center pointer-events-none select-none z-0"
        />

        {/* Tmavé překrytí (Overlay) – roztáhne se přes celou fotku */}
        <div className="absolute inset-0 bg-black/30 z-10 pointer-events-none"></div>

        {/* Hlavní sjednocený designový box s textem */}
        <div className="relative z-20 mx-4 max-w-3xl w-full bg-black/65 backdrop-blur-md border border-white/10 rounded-2xl p-6 md:p-10 text-center text-white shadow-2xl">
          <div>
            {/* Hlavní nadpis – Původní velikost, bez zalamování */}
            <h1 className="text-3xl md:text-7xl font-bold mb-4 drop-shadow-2xl whitespace-nowrap" data-testid="hero-title" style={{ textShadow: '0 4px 20px rgba(0,0,0,0.9), 0 2px 8px rgba(0,0,0,0.8)' }}>
              Pískáč Postřelmov
            </h1>

            {/* Jemná dělící linka pro optické oddělení v novém bloku */}
            <div className="w-16 h-1 bg-blue-500 mx-auto mb-5 rounded-full opacity-80"></div>

            {/* Podnadpis – Nyní čistě jako součást bloku bez černé krabice okolo */}
            <p className="text-lg md:text-4xl font-semibold tracking-wide whitespace-nowrap" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.9), 0 1px 4px rgba(0,0,0,0.8)' }}>
              TÁBOŘIŠTĚ NEJEN PRO VODÁKY
            </p>
          </div>
        </div>
      </section>

      {/* Spojená sekce: O nás a Video vedle sebe na velkých displejích */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            
            {/* LEVÝ SLOUPEC: O nás */}
            <div className="prose prose-lg max-w-none">
              <h2 className="text-4xl font-bold mb-6 text-gray-800 text-center lg:text-left">O nás</h2>
              <p className="text-lg text-gray-700 mb-6">
                Původně sloužil areál k těžbě písku. V současné době je využíván k rekreačním aktivitám. Zahájení sezóny 30.4.2026.
              </p>
              <ul className="space-y-3 text-gray-700 list-none pl-0">
                <li className="flex items-start">
                  <span className="text-blue-600 font-bold mr-2">–</span>
                  <span>vodní plocha je přes léto využívána k rybaření, lze se i koupat</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 font-bold mr-2">–</span>
                  <span>v zimě lze využít zamrzlou hladinu Pískáče k bruslení (na vlastní nebezpečí)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 font-bold mr-2">–</span>
                  <span>původní budova je přestavěna jako stánek s občerstvením</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 font-bold mr-2">–</span>
                  <span>součástí areálu je sociální zařízení</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 font-bold mr-2">–</span>
                  <span>k dispozici je přístup k internetu (Wi-Fi)</span>
                </li>
              </ul>
            </div>

            {/* PRAVÝ SLOUPEC: Video z areálu */}
            <div className="flex flex-col justify-center">
              <div className="text-center lg:text-left mb-6">
                <h2 className="text-4xl font-bold text-gray-800 mb-2">Video z areálu</h2>
                <p className="text-gray-600">Podívejte se na video z našeho tábořiště</p>
              </div>

              <div className="bg-gray-100 rounded-xl overflow-hidden shadow-lg w-full">
                {videoUrl ? (
                  <div className="aspect-video">
                    <VideoPlayer url={videoUrl} />
                  </div>
                ) : (
                  <div className="aspect-video flex items-center justify-center bg-gradient-to-br from-blue-50 to-cyan-50">
                    <div className="text-center px-4">
                      <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-4">
                        <Play className="w-10 h-10 text-blue-600" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-800 mb-2">Připraveno pro video</h3>
                      <p className="text-sm text-gray-600">
                        Vložte URL videa do kódu
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Opening Hours Section */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 mb-4">
              <Clock className="w-12 h-12 text-blue-600" />
              <h2 className="text-4xl font-bold text-gray-800">Otevírací doba</h2>
              <LockOpen className="w-12 h-12 text-yellow-500" />
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl shadow-lg p-8 max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <p className="text-xl text-gray-700 font-semibold mb-2">
                Otevřeno v sezóně od května do září
              </p>
              <p className="text-gray-600 italic">
                Otevírací doba se může lišit z důvodu počasí
              </p>
            </div>

            <div className="space-y-4 mb-8">
              <div className="flex justify-between items-center p-4 bg-white rounded-lg hover:bg-blue-50 transition-colors">
                <span className="font-semibold text-gray-800">po-čt</span>
                <span className="text-gray-700">16:00-20:00 hod.</span>
              </div>
              <div className="flex justify-between items-center p-4 bg-white rounded-lg hover:bg-blue-50 transition-colors">
                <span className="font-semibold text-gray-800">pátek</span>
                <span className="text-gray-700">16:00-21:00 hod.</span>
              </div>
              <div className="flex justify-between items-center p-4 bg-white rounded-lg hover:bg-blue-50 transition-colors">
                <span className="font-semibold text-gray-800">sobota</span>
                <span className="text-gray-700">14:00-21:00 hod.</span>
              </div>
              <div className="flex justify-between items-center p-4 bg-white rounded-lg hover:bg-blue-50 transition-colors">
                <span className="font-semibold text-gray-800">neděle</span>
                <span className="text-gray-700">14:00-20:00 hod.</span>
              </div>
            </div>

            <div className="border-l-4 border-red-500 bg-red-50 p-4 rounded">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                <p className="text-gray-800">
                  Dovolujeme si Vás upozornit, že nepřijímáme bezkontaktní platby.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
